import { annaSelvityspyynnonKirjausrivinPuoli, annaSelvityspyynnonSumma, Selvityspyynto } from '@lemon/core-firebase/model/selvitettavat'
import { LocalDate, Timestamp } from '@lemon/core/model/common'
import { Kirjaus, Kirjausrivi } from '../model/kirjanpito'

// Kuittauskirjauksen rivi kantaa jo vastattua pyyntöä, ja alkusaldojen 1777-saldo on avaavaa tasetta eikä asiakkaalta kysyttävä tapahtuma.
const KANAVAT_ILMAN_SELVITYSPYYNTOJA: ReadonlySet<Kirjaus['kanava']> = new Set<Kirjaus['kanava']>(['selvityskuittausautom', 'alkusaldoautom'])

export const tarvitseekoKirjausSelvityspyynnot = (kirjaus: Pick<Kirjaus, 'kanava'>): boolean => !KANAVAT_ILMAN_SELVITYSPYYNTOJA.has(kirjaus.kanava)

export const luoSelvityspyynto = (asiakas: { avain: string; asiakasId: number | string }, kirjaus: Kirjaus, rivi: Kirjausrivi, pvml: LocalDate, nyt: Timestamp): Selvityspyynto => {
  const selvityspyynto: Selvityspyynto = {
    avain: rivi.av,
    asiakasId: asiakas.asiakasId + '',
    asiakasAvain: asiakas.avain,
    kirjausAvain: kirjaus.avain,
    tila: 'avoin',
    maksunsaaja: kirjaus.kirjauksenSaajaTaiMaksaja ?? '',
    kirjauksenViesti: kirjaus.kirjauksenViesti ?? '',
    summa: annaSelvityspyynnonSumma(rivi),
    kirjausrivinPuoli: annaSelvityspyynnonKirjausrivinPuoli(rivi),
    tyyppi: 'lisatietopyynto',
    pvml: pvml,
    kysymys: rivi.s ?? '',
    luotu: nyt,
    luoja: 'lemonator_system',
    paivitetty: nyt,
    paivittaja: 'lemonator_system',
    poistettu: false,
  }
  const maksutapa = Number(kirjaus.maksutavanTunniste)
  if (Number.isFinite(maksutapa) && maksutapa > 0) {
    selvityspyynto.maksutapa = maksutapa
  }
  return selvityspyynto
}

/**
 * Jokaisella selvitettävän tilin rivillä on oltava selvityspyyntö, jotta asiakas näkee sen kirjanpitäjän hyväksyttyä kirjauksen.
 * Rivejä syntyy tilille myös automaateista ja kopioimalla, joten pyyntö täydennetään tallennuksessa eikä rivin luontihetkellä.
 */
export const lisaaPuuttuvatSelvityspyynnot = (
  asiakas: { avain: string; asiakasId: number | string },
  kirjaus: Kirjaus,
  onkoSelvitettavaTili: (tilinumero: string) => boolean,
  pvml: LocalDate,
  nyt: Timestamp,
): Kirjausrivi[] => {
  if (!tarvitseekoKirjausSelvityspyynnot(kirjaus)) {
    return []
  }
  const lisatyt = (kirjaus.rivit ?? []).filter(rivi => !rivi.sp && onkoSelvitettavaTili(rivi.t))
  for (const rivi of lisatyt) {
    rivi.sp = luoSelvityspyynto(asiakas, kirjaus, rivi, pvml, nyt)
  }
  if (lisatyt.length > 0) {
    kirjaus.selvityspyyntoAvaimet = [...new Set([...(kirjaus.selvityspyyntoAvaimet ?? []), ...lisatyt.map(rivi => rivi.av)])]
  }
  return lisatyt
}

/**
 * Debet- tai kredit-summaa voi muuttaa pyynnön luonnin ja dialogissa muokkaamisen jälkeen, joten avoimen pyynnön summa ja puoli
 * otetaan riviltä tallennushetkellä. Vastatun kierroksen summa jää siksi, millä asiakas vastasi.
 */
export const paivitaAvointenSelvityspyyntojenSummat = (kirjaus: Pick<Kirjaus, 'rivit'>): void => {
  for (const rivi of kirjaus.rivit ?? []) {
    if (rivi.sp?.tila === 'avoin') {
      rivi.sp.summa = annaSelvityspyynnonSumma(rivi)
      rivi.sp.kirjausrivinPuoli = annaSelvityspyynnonKirjausrivinPuoli(rivi)
    }
  }
}

const _kierrokset = (selvityspyynto: Selvityspyynto | undefined): Selvityspyynto[] => (selvityspyynto ? [selvityspyynto, ...(selvityspyynto.aikaisemmat ?? [])] : [])

/** Asiakkaan vastauksena lähettämät tositteet kaikilta kierroksilta. */
export const annaSelvityspyyntojenVastaustositteet = (kirjaus: Pick<Kirjaus, 'rivit'>): Set<string> =>
  new Set((kirjaus.rivit ?? []).flatMap(rivi => _kierrokset(rivi.sp).flatMap(kierros => kierros.vastausTositeAvaimet ?? [])))

const _normalisoiValilyonnit = (teksti: string | null | undefined): string => (teksti ?? '').replace(/\s+/g, ' ').trim()

const _kappaleet = (teksti: string | null | undefined): string[] =>
  (teksti ?? '')
    .split('\n\n')
    .map(kappale => _normalisoiValilyonnit(kappale))
    .filter(kappale => kappale.length > 0)

/** Asiakkaan vastaukset kaikilta kierroksilta kappaleittain; Lemonaid kirjoittaa ne tositteiden lisätietoihin omiksi kappaleikseen. */
export const annaSelvityspyyntojenVastauskappaleet = (kirjaus: Pick<Kirjaus, 'rivit'>): string[] => [
  ...new Set((kirjaus.rivit ?? []).flatMap(rivi => _kierrokset(rivi.sp).flatMap(kierros => _kappaleet(kierros.vastaus)))),
]

/**
 * Vastaus näkyy kirjaukselle selvityspyynnön vastauksena, joten tositteen lisätiedoista Lemonaid-viestiin tuodaan vain
 * muut kappaleet. Muuten sama vastaus toistuisi viestissä jokaisen liitetyn tositteen mukana.
 */
export const poistaVastauskappaleetSelitteesta = (selite: string | null | undefined, vastauskappaleet: string[]): string => {
  if (vastauskappaleet.length === 0) {
    return selite ?? ''
  }
  const poistettavat = new Set(vastauskappaleet)
  return (selite ?? '')
    .split('\n\n')
    .filter(kappale => !poistettavat.has(_normalisoiValilyonnit(kappale)))
    .join('\n\n')
}

const _escapeRegExp = (teksti: string) => teksti.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

/**
 * Tosite voi ehtiä kirjaukselle ennen selvityspyynnön vastausta, jolloin vastaus on jo tullut Lemonaid-viestiin lisätiedoista.
 * Viesti on tallennettu välilyönnit normalisoituna, joten kappale poistetaan kokonaisena sanarajoilla, ettei lyhyt vastaus
 * (esim. "Ok") syö osaa toisesta sanasta.
 */
export const poistaVastauskappaleetViestista = (viesti: string | null | undefined, vastauskappaleet: string[]): string | null => {
  if (vastauskappaleet.length === 0) {
    return viesti ?? null
  }
  let tulos = _normalisoiValilyonnit(viesti)
  for (const kappale of vastauskappaleet) {
    tulos = _normalisoiValilyonnit(tulos.replace(new RegExp(`(^|\\s)${_escapeRegExp(kappale)}(?=\\s|$)`, 'g'), ' '))
  }
  return tulos || null
}

/** Vastauksen tekstin kappaleet sellaisinaan Lemonaid-viestistä poistamista varten. */
export const annaVastauksenKappaleet = (vastaus: string | null | undefined): string[] => _kappaleet(vastaus)

/**
 * Vanhassa kanavassa asiakas vastaa kirjoittamalla tositteen lisätietoihin. Jos lisätiedoissa ei ole mitään, mitä
 * kirjauksen Lemonaid-viestissä ei jo ollut, asiakas ei kirjoittanut uutta, ja kutsuja käyttää yleistä tekstiä.
 */
export const annaVanhanKanavanVastaus = (selite: string | null | undefined, lemonaidViestiEnnen: string | null | undefined): string | null => {
  const vastaus = (selite ?? '').trim()
  if (!vastaus) {
    return null
  }
  const ennen = _normalisoiValilyonnit(lemonaidViestiEnnen)
  if (ennen && ennen.includes(_normalisoiValilyonnit(vastaus))) {
    return null
  }
  return vastaus
}

const _jarjestaLuontiajanMukaan = (kierrokset: Selvityspyynto[]): Selvityspyynto[] => [...kierrokset].sort((a, b) => (_millit(a.luotu) ?? 0) - (_millit(b.luotu) ?? 0))

/** Tallennetun pyynnön vastatut kierrokset, joita tallennettava ei tunne vastattuina. Kierros tunnistetaan luontiajasta. */
export const annaTuntemattomatVastatutKierrokset = (tallennettu: Selvityspyynto | undefined, tallennettava: Selvityspyynto | undefined): Selvityspyynto[] => {
  const tunnetutVastatut = new Set(_kierrokset(tallennettava).filter(kierros => kierros.tila === 'vastattu').map(kierros => _millit(kierros.luotu)))
  return _kierrokset(tallennettu).filter(kierros => kierros.tila === 'vastattu' && !tunnetutVastatut.has(_millit(kierros.luotu)))
}

/**
 * Tuonti kirjoittaa asiakkaan vastauksen kirjaukselle, vaikka kirjanpitäjällä on kirjaus auki vanhoin tiedoin. Tallennettava versio ei
 * silloin tunne vastausta: se voi kantaa saman kierroksen avointa pyyntöä, uutta pyyntöä tai rivin pyyntö on poistettu tilin vaihdossa.
 * Vastaus säilytetään tilistä riippumatta. Tarkoituksellinen uusi kierros tuntee vastatun kierroksen aikaisemmissa, joten se menee läpi.
 */
export const sailytaVastatutSelvityspyynnot = (kirjaus: Kirjaus, tallennettu: Kirjaus | null): Kirjausrivi[] => {
  const tallennetutPyynnot = new Map((tallennettu?.rivit ?? []).filter(rivi => rivi.sp).map(rivi => [rivi.av, rivi.sp]))
  const sailytetyt: Kirjausrivi[] = []
  for (const rivi of kirjaus.rivit ?? []) {
    const tallennettuPyynto = tallennetutPyynnot.get(rivi.av)
    const tuntemattomat = annaTuntemattomatVastatutKierrokset(tallennettuPyynto, rivi.sp)
    if (tuntemattomat.length === 0) {
      continue
    }
    const tuntemattomienLuontiajat = new Set(tuntemattomat.map(kierros => _millit(kierros.luotu)))
    const vastattuOnNykyinenKierros = tuntemattomat.includes(tallennettuPyynto)
    if (!rivi.sp || vastattuOnNykyinenKierros || tuntemattomienLuontiajat.has(_millit(rivi.sp.luotu))) {
      rivi.sp = tallennettuPyynto
    } else {
      const aikaisemmat = (rivi.sp.aikaisemmat ?? []).filter(kierros => !tuntemattomienLuontiajat.has(_millit(kierros.luotu)))
      rivi.sp = { ...rivi.sp, aikaisemmat: _jarjestaLuontiajanMukaan([...aikaisemmat, ...tuntemattomat]) }
    }
    sailytetyt.push(rivi)
  }
  if (sailytetyt.length > 0) {
    kirjaus.selvityspyyntoAvaimet = [...new Set([...(kirjaus.selvityspyyntoAvaimet ?? []), ...sailytetyt.map(rivi => rivi.av)])]
  }
  return sailytetyt
}

/**
 * Backendin tallennus: kutsuja (esim. tilintarkastajan näkymä) ei välttämättä tunne rivin pyyntöä, jolloin se puuttuisi
 * tallennettavasta kirjauksesta. Tallennettu pyyntö palautetaan ennen kuin puuttuvat luodaan, jottei vastaus katoa.
 */
export const taydennaSelvityspyynnotTallennettaessa = (
  asiakas: { avain: string; asiakasId: number | string },
  kirjaus: Kirjaus,
  tallennettu: Kirjaus | null,
  onkoSelvitettavaTili: (tilinumero: string) => boolean,
  pvml: LocalDate,
  nyt: Timestamp,
): { sailytetyt: Kirjausrivi[]; palautetut: Kirjausrivi[]; lisatyt: Kirjausrivi[] } => {
  const sailytetyt = sailytaVastatutSelvityspyynnot(kirjaus, tallennettu)
  const tallennetutPyynnot = new Map((tallennettu?.rivit ?? []).filter(rivi => rivi.sp).map(rivi => [rivi.av, rivi.sp]))
  const palautetut = (kirjaus.rivit ?? []).filter(rivi => !rivi.sp && onkoSelvitettavaTili(rivi.t) && tallennetutPyynnot.has(rivi.av))
  for (const rivi of palautetut) {
    rivi.sp = tallennetutPyynnot.get(rivi.av)
  }
  if (palautetut.length > 0) {
    kirjaus.selvityspyyntoAvaimet = [...new Set([...(kirjaus.selvityspyyntoAvaimet ?? []), ...palautetut.map(rivi => rivi.av)])]
  }
  const lisatyt = lisaaPuuttuvatSelvityspyynnot(asiakas, kirjaus, onkoSelvitettavaTili, pvml, nyt)
  paivitaAvointenSelvityspyyntojenSummat(kirjaus)
  return { sailytetyt, palautetut, lisatyt }
}

export const onkoKirjauksellaSelvitettavaaRivia = (kirjaus: Pick<Kirjaus, 'rivit'>, onkoSelvitettavaTili: (tilinumero: string) => boolean): boolean =>
  (kirjaus.rivit ?? []).some(rivi => onkoSelvitettavaTili(rivi.t))

const _millit = (aika: Timestamp | undefined | null): number | null => (aika && typeof aika.toMillis === 'function' ? aika.toMillis() : null)

/** Sama kierros tunnistetaan luontiajasta: kierrätys antaa uudelle kierrokselle uuden luontiajan. */
export const onkoVastattuSamallaKierroksella = (kirjauksenPyynto: Selvityspyynto | undefined, lemonaidinVastattu: Selvityspyynto | undefined): boolean => {
  if (!kirjauksenPyynto || !lemonaidinVastattu || kirjauksenPyynto.tila === 'vastattu' || lemonaidinVastattu.tila !== 'vastattu') {
    return false
  }
  const kirjauksella = _millit(kirjauksenPyynto.luotu)
  return kirjauksella !== null && kirjauksella === _millit(lemonaidinVastattu.luotu)
}

const _asetaVastaus = (kohde: Selvityspyynto, lemonaidinVastattu: Selvityspyynto, nyt: Timestamp) => {
  kohde.vastaus = lemonaidinVastattu.vastaus ?? null
  kohde.tila = 'vastattu'
  kohde.vastattu = lemonaidinVastattu.vastattu ?? nyt
  // Asiakkaan lisäämät tositteet näkyvät kirjanpitäjälle heti, vaikka tositeputki toisi ne kirjaukselle vasta myöhemmin
  kohde.vastausTositeAvaimet = lemonaidinVastattu.vastausTositeAvaimet ?? []
}

/** Saman pyynnön lisävastauksen tuonnit voivat ajautua rinnakkain, eikä vanhempi vastaus saa korvata jo kirjattua uudempaa. */
const _onkoKirjattuVastausUudempi = (kirjattu: Selvityspyynto | undefined, lemonaidinVastattu: Selvityspyynto): boolean => {
  const kirjattuVastattu = kirjattu?.tila === 'vastattu' ? _millit(kirjattu.vastattu) : null
  const lemonaidinVastattuAika = _millit(lemonaidinVastattu.vastattu)
  return kirjattuVastattu !== null && lemonaidinVastattuAika !== null && kirjattuVastattu > lemonaidinVastattuAika
}

/**
 * Tuonti löytää rivin avaimella eikä tilillä, koska kirjanpitäjä on voinut vaihtaa tilin ja tallennus poistaa rivin pyynnön ennen tuontia.
 * Jos rivillä on jo vastauksesta tietämättä luotu uusi kierros, asiakkaalle näkyvä uusi kysymys jää voimaan ja vastaus menee aikaisempiin.
 * Palauttaa false, jos rivillä on jo saman kierroksen uudempi vastaus eikä riviä muutettu.
 */
export const kirjaaVastausRiville = (rivi: Kirjausrivi, lemonaidinVastattu: Selvityspyynto, nyt: Timestamp): boolean => {
  if (!rivi.sp) {
    rivi.sp = { ...lemonaidinVastattu }
    _asetaVastaus(rivi.sp, lemonaidinVastattu, nyt)
    return true
  }
  const kirjauksella = _millit(rivi.sp.luotu)
  const lemonaidissa = _millit(lemonaidinVastattu.luotu)
  const eriKierros = rivi.sp.tila === 'avoin' && kirjauksella !== null && lemonaidissa !== null && kirjauksella !== lemonaidissa
  if (!eriKierros) {
    if (_onkoKirjattuVastausUudempi(rivi.sp, lemonaidinVastattu)) {
      return false
    }
    _asetaVastaus(rivi.sp, lemonaidinVastattu, nyt)
    return true
  }
  if (_onkoKirjattuVastausUudempi(rivi.sp.aikaisemmat?.find(kierros => _millit(kierros.luotu) === lemonaidissa), lemonaidinVastattu)) {
    return false
  }
  const vastattuKierros: Selvityspyynto = { ...lemonaidinVastattu }
  delete vastattuKierros.aikaisemmat
  _asetaVastaus(vastattuKierros, lemonaidinVastattu, nyt)
  const aikaisemmat = (rivi.sp.aikaisemmat ?? []).filter(kierros => _millit(kierros.luotu) !== lemonaidissa)
  rivi.sp.aikaisemmat = _jarjestaLuontiajanMukaan([...aikaisemmat, vastattuKierros])
  return true
}

/** Palauttaa rivit, joissa pyynnön rivi osoittaa kuittauskirjaukseen, tai null, jos riviä tai pyyntöä ei ole tai linkki on jo ajan tasalla. */
export const paivitaKuittauskirjauksenLinkki = (rivit: Kirjausrivi[] | undefined, riviAvain: string, kuittauskirjausAvain: string): Kirjausrivi[] | null => {
  const rivi = (rivit ?? []).find(r => r.av === riviAvain)
  if (!rivi?.sp || rivi.sp.kuittauskirjausAvain === kuittauskirjausAvain) {
    return null
  }
  return rivit.map(r => (r === rivi ? { ...r, sp: { ...r.sp, kuittauskirjausAvain } } : r))
}
